import { useContent } from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  useGetPersonalDataQuery,
  useSaveCountryOfTaxResidenceTaxDataMutation,
} from 'store/slices/personalData-api';
import { taxInformationContent } from '../taxInformation.content';
import {
  Button,
  Heading,
  SelectButtonLikeInput,
  Typography,
} from '@altify/components';
import { useGetCountriesQuery } from 'store/slices/references-api';
import { useState } from 'react';
import CountrySelectionModal from '../components/CountrySelectionModal/CountrySelectionModal';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';

export const Route = createFileRoute(
  '/_authenticated/tax-information/add-country-of-tax-residence',
)({
  component: CountryOfTaxResidenceTaxInformation,
});

function CountryOfTaxResidenceTaxInformation() {
  const router = useRouter();
  const {
    data: personalData,
    isLoading: personalDataLoading,
    isFetching: personalDataFetching,
  } = useGetPersonalDataQuery();
  const { data: countries, isLoading: countriesIsLoading } =
    useGetCountriesQuery();
  const [saveCountryOfTaxResidence, saveCountryOfTaxResidenceResult] =
    useSaveCountryOfTaxResidenceTaxDataMutation();
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    personalData?.countryOfTaxResidence ?? undefined,
  );
  const localizedContent = useContent(taxInformationContent);
  const [showCountrySearch, setShowCountrySearch] = useState(false);
  const handleSubmit = async () => {
    if (selectedCountry) {
      const result = await saveCountryOfTaxResidence(selectedCountry);
      if (!result.error) {
        router.navigate({
          to: '/tax-information/add-tax-identification-number',
        });
      }
    }
  };

  const showLoader =
    personalDataLoading || personalDataFetching || countriesIsLoading;

  if (showLoader) {
    return <CustomLoader page />;
  }
  return (
    <div className="w-full items-center">
      <div className="flex flex-col w-full items-center flex-1">
        <div className="flex flex-col items-center mb-10">
          <Heading level={4} className="mb-3 mt-5 text-center">
            {localizedContent.countryOfTaxResidenceTitle}
          </Heading>
        </div>
        <>
          <SelectButtonLikeInput
            label={localizedContent.countryOfTaxResidence}
            placeholder={localizedContent.countryOfTaxResidence}
            value={countries?.find(c => c.code === selectedCountry)?.name}
            onPress={() => setShowCountrySearch(true)}
          />
          <CountrySelectionModal
            isOpen={showCountrySearch}
            handleClose={() => setShowCountrySearch(false)}
            onSelect={setSelectedCountry}
          />
        </>
      </div>
      <div className="w-full pb-2 pt-10 items-center flex flex-col">
        {saveCountryOfTaxResidenceResult.error && (
          <Typography className="my-5 text-center text-error">
            {localizedContent.unexpectedErrorOccurred}
          </Typography>
        )}
        <Button
          size="large"
          color="primary"
          disabled={!selectedCountry}
          onPress={handleSubmit}
          loading={saveCountryOfTaxResidenceResult.isLoading}
          text={localizedContent.continue}
          className="w-full"
        />
      </div>
    </div>
  );
}

export default CountryOfTaxResidenceTaxInformation;
